// Dependencias
import type { TFunction } from "i18next";
import { useState } from "react";

// Iconos
import GeneralIcon from "../icons/GeneralIcon";
import Icon from "../icons/Icon";

import { useDarkMode } from "../../../../../context/ThemeContext";

interface MenuButtonOption {
  section: string;
  value: string;
  label: string;
  action: () => void;
}

interface MenuButtonProps {
  id?: string;
  view: string;
  currentView: string;
  handleClick: (view: string) => void;
  validView: string[];
  title: string;
  submenu?: boolean;
  expandableMenu: boolean;
  options?: MenuButtonOption[];
  darkMode?: boolean;
  t?: TFunction;
  disabled?: boolean;
}

function MenuButton({
  id,
  view,
  handleClick,
  validView,
  title,
  submenu,
  expandableMenu,
  options = [],
  t,
  disabled = false,
}: MenuButtonProps) {
  // Modo oscuro
  const { isDarkMode } = useDarkMode();

  const currentView = localStorage.getItem("view") || "general";
  const isActive = validView.includes(currentView);

  const [viewSubmenu, setViewSubmenu] = useState(false);

  // Renderiza el icono correspondiente en el botón
  const renderIcon = () => {
    switch (view) {
      case "telemetry":
        return (
          <Icon view={view} currentView={currentView} validView={validView} />
        );
      case "truckYards":
        return (
          <Icon view={view} currentView={currentView} validView={validView} />
        );
      case "platformArea":
        return (
          <Icon view={view} currentView={currentView} validView={validView} />
        );
      case "security":
        return (
          <Icon view={view} currentView={currentView} validView={validView} />
        );
      default:
        return (
          <GeneralIcon
            view={view}
            currentView={currentView}
            validView={validView}
          />
        );
    }
  };

  return (
    <div id={id ? id : ""}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          submenu ? setViewSubmenu(!viewSubmenu) : handleClick(view);
        }}
        className={`flex items-center justify-between 
          py-1 px-2 font-bold mb-1 rounded-lg group
          ${
            disabled
              ? "text-gray-400 cursor-not-allowed"
              : isActive
              ? isDarkMode
                ? "text-white bg-[#0052A2]"
                : "text-white bg-[#1C93FF]"
              : isDarkMode
              ? "text-white hover:bg-[#004080]"
              : "text-black hover:text-white hover:bg-[#197CD6]"
          }
          ${expandableMenu ? "w-full" : "w-fit py-2"}`}
        style={disabled ? { pointerEvents: "none" } : {}}
      >
        <div className="flex items-center">
          {renderIcon()}
          {expandableMenu && (
            <span className="ms-3 text-left transition duration-75">
              {t ? t(title) : title}
            </span>
          )}
        </div>
        {submenu && expandableMenu && (
          <svg
            className={`w-2.5 h-2.5 mr-1 ${
              viewSubmenu ? "rotate-180" : "rotate-0"
            }`}
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 10 6"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="m1 1 4 4 4-4"
            />
          </svg>
        )}
      </button>
      {submenu && expandableMenu && viewSubmenu && (
        <div className="ml-8 flex flex-col gap-0.5 text-xs">
          {options.map((option, index) => (
            <button
              key={index}
              className={
                isDarkMode
                  ? `hover:bg-dark-border p-2 rounded-lg text-start`
                  : `hover:bg-[#f5f5f5] p-2 rounded-lg text-start`
              }
              onClick={() => option.action()}
            >
              {t ? t(option.label) : option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default MenuButton;
